
import Enumerable from "linq";
import Papa from "papaparse";
import { Component, Mixins, } from "vue-property-decorator";
import {
  FixedPhraseRootReference,
  IFixedPhraseReferenceTreeData,
  FixedPhraseReference,
  IFixedPhraseReferenceTreeNode,
  IFixedPhraseContent,
  IFixedPhraseFolderContent,
  IFixedPhraseFolderRootContent,
  instanceOfIFixedPhraseFolderRootContent,
  IFixedPhraseImportContent,
} from "../../../../../domain/document";
import { Directory } from "../../../../../domain/drawing";
import { DirectoryTreeNodesFactory } from "../../../../system/utilities/DirectoryTreeNodesFactory";

@Component
export default class FixedPhraseImportDialogMixin extends Mixins() {
  data(): any {
    return {
      isLoading: false,
      isConfirmed: false,
      nodes: [],
      selectedFile: null as File,
      factory: null as DirectoryTreeNodesFactory<number, FixedPhraseReference>,
      selectedFixedPhrase: null,
      areNodesChecked: false,
    };
  }
  isLoading: boolean;
  isConfirmed: boolean;
  nodes: IFixedPhraseReferenceTreeData[];
  selectedFile: File;
  factory: DirectoryTreeNodesFactory<number, FixedPhraseReference>;
  selectedFixedPhrase: string;
  areNodesChecked: boolean;

  nodeClass(node: IFixedPhraseReferenceTreeNode): string {
    if (node.data instanceof Object) {
      if (node.data.source instanceof Directory) {
        return node.expanded
          ? "tree-icon-directory-expand"
          : "tree-icon-directory";
      }
      return "tree-icon-fixed-phrase";
    }
    return "";
  }

  nodeLabel(node: IFixedPhraseReferenceTreeNode): string {
    return node.label;
  }

  toFixedPhraseNode(entity: FixedPhraseReference, i: number): IFixedPhraseReferenceTreeData {
    return {
      id: i,
      label: entity.name ? entity.name : entity.text,
      iconClass: "tree-icon-fixed-phrase",
      source: entity,
      children: [],
    };
  }

  private toFixedPhraseDirectories(beans: IFixedPhraseFolderContent[]): Directory<number, FixedPhraseReference>[] {
    return Enumerable.from(beans)
      .select((b, i) => this.toFixedPhraseDirectory(b, i))
      .toArray();
  }

  private toFixedPhraseDirectory(bean: IFixedPhraseFolderContent, index: number): Directory<number, FixedPhraseReference> {
    return new Directory(
      index,
      bean.name,
      this.toFixedPhraseDirectories(bean.folders),
      this.toFixedPhrases(bean.phrase)
    );
  }

  private toFixedPhrases(beans: IFixedPhraseContent[]): FixedPhraseReference[] {
    return Enumerable.from(beans)
      .select(b => this.toFixedPhrase(b))
      .toArray();
  }

  private toFixedPhrase(bean: IFixedPhraseContent): FixedPhraseReference {
    return new FixedPhraseReference(
      bean.name,
      bean.text
    );
  }

  onSelectionChanged(data: IFixedPhraseReferenceTreeData): void {
    this.selectedFixedPhrase = (data && data.source instanceof FixedPhraseReference)
      ? data.source.text
      : "";
  }

  async checkImportDataAndUpdateNodes(rootContent: IFixedPhraseFolderRootContent): Promise<void> {
    const importData = await this.checkEmptyTemplate(rootContent);
    if (importData === undefined) {
      this.selectedFile = null;
      return;
    }
    this.updateNodes(importData);
  }

  readCsvFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.parseCsvFileDataAndUpdateNodes(e.target.result);
    };
    reader.onerror = (e: any) => {
      throw e;
    };
    reader.readAsText(file, "Shift_JIS");
  }

  parseCsvFileDataAndUpdateNodes(data: any): void {
    try {
      const result = Papa.parse(data, { header: false });
      const importContents = Enumerable.from(result.data as string[][]).select(s => this.toFixedPhraseImportContent(s)).toArray();
      const rootContent = this.toFixedPhraseFolderRootContent(importContents.filter(c => (c.parentNames && c.parentNames.length > 0)));
      this.checkImportDataAndUpdateNodes(rootContent);
    } catch (e) {
      this.$error("読み込み失敗", "CSVファイルの読み込みに失敗しました。", e);
      throw e;
    }
  }

  readJsonFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.parseJsonFileDataAndUpdateNodes(e.target.result);
    };
    reader.onerror = (e: any) => {
      throw e;
    };
    reader.readAsText(file, "UTF-8");
  }

  parseJsonFileDataAndUpdateNodes(data: any): void {
    try {
      const parsed = JSON.parse(data);
      if (!instanceOfIFixedPhraseFolderRootContent(parsed)) {
        throw new Error();
      }
      this.checkImportDataAndUpdateNodes(parsed as IFixedPhraseFolderRootContent);
    } catch (e) {
      this.$error("読み込み失敗", "JSONファイルの読み込みに失敗しました。", e);
      throw e;
    }
  }

  updateNodes(rootContent: IFixedPhraseFolderRootContent): void {
    const directories = new FixedPhraseRootReference(rootContent.tableId, rootContent.templateId, this.toFixedPhraseDirectories(rootContent.folders));
    this.factory = new DirectoryTreeNodesFactory(this.toFixedPhraseNode);
    this.nodes = this.factory.create(directories ? directories.folders : null);
  }

  toFixedPhraseImportContent(contents: string[]): IFixedPhraseImportContent {
    return {
      name: contents[0],
      phrase: contents[1],
      parentNames: contents.splice(2, contents.length),
    };
  }

  toFixedPhraseFolderRootContent(importContents: IFixedPhraseImportContent[]): IFixedPhraseFolderRootContent {
    return {
      tableId: "",
      templateId: "",
      folders: this.toFixedPhraseFolderContents(importContents),
    };
  }

  toFixedPhraseFolderContents(importContents: IFixedPhraseImportContent[]): IFixedPhraseFolderContent[] {
    const maxLength = Enumerable.from(importContents).select(i => i.parentNames.length).max();
    const addedFolderName: string[] = [];
    const folders: IFixedPhraseFolderContent[] = [];
    for (let i = 0; i < maxLength; i++) {
      importContents.forEach((c => {
        if (i === 0) {
          if (!addedFolderName.includes(c.parentNames[i])) {
            folders.push({
              name: c.parentNames[i],
              folders: [],
              phrase: c.parentNames.length === (i + 1) ? [ { name: c.name, text: c.phrase }] : [],
            });
            addedFolderName.push(c.parentNames[i]);
          } else if (addedFolderName.includes(c.parentNames[i]) && (c.parentNames.length === (i + 1))) {
            const index = folders.findIndex(f => f.name === c.parentNames[i]);
            folders[index].phrase = folders[index].phrase.concat([ { name: c.name, text: c.phrase }]);
          }
        } else if (i > 0 && c.parentNames.length > i) {
          if (!addedFolderName.includes(c.parentNames[i]) && addedFolderName.includes(c.parentNames[i - 1])) {
            this.insertImportFolders(folders, c, i);
            addedFolderName.push(c.parentNames[i]);
          } else if (addedFolderName.includes(c.parentNames[i]) && addedFolderName.includes(c.parentNames[i - 1])) {
            this.updateImportFolderPhrase(folders, c, i);
          }
        }
      }));
    }
    return folders;
  }

  insertImportFolders(original: IFixedPhraseFolderContent[], content: IFixedPhraseImportContent, index: number): IFixedPhraseFolderContent[] {
    original.forEach(o => {
      if (o.name === content.parentNames[index - 1]) {
        o.folders = o.folders.concat({
          name: content.parentNames[index],
          folders: [],
          phrase: content.parentNames.length === (index + 1) ? [ { name: content.name, text: content.phrase }] : [],
        });
        return;
      }
      this.insertImportFolders(o.folders, content, index);
    });
    return original;
  }

  updateImportFolderPhrase(original: IFixedPhraseFolderContent[], content: IFixedPhraseImportContent, index: number): IFixedPhraseFolderContent[] {
    original.forEach(o => {
      if (o.name === content.parentNames[index] && content.parentNames.length === (index + 1)) {
        o.phrase = o.phrase.concat([ { name: content.name, text: content.phrase }]);
        return;
      }
      this.updateImportFolderPhrase(o.folders, content, index);
    });
    return original;
  }

  private async checkEmptyTemplate(data: IFixedPhraseFolderRootContent): Promise<IFixedPhraseFolderRootContent> {
    return this.hasNodeEmptyTemplate(data)
      ? this.confirmAndExtractEmptyTemplate(data)
      : data;
  }

  private hasNodeEmptyTemplate(data: IFixedPhraseFolderRootContent): boolean {
    return data.folders.some(n => this.hasEmptyTemplate(n));
  }

  private hasEmptyTemplate(folderContent: IFixedPhraseFolderContent): boolean {
    if (folderContent.phrase && folderContent.phrase.some(p => !p.text)) { return true; }
    return folderContent.folders.some(f => this.hasEmptyTemplate(f));
  }

  private async confirmAndExtractEmptyTemplate(data: IFixedPhraseFolderRootContent): Promise<IFixedPhraseFolderRootContent> {
    const confirmResult = await this.confirmToExportEmptyTemplate();
    if (confirmResult === 0) { return data; }
    if (confirmResult === 1) { return this.extractEmptyTemplate(data); }
    if (confirmResult === 2) { return undefined; }
  }

  private async confirmToExportEmptyTemplate(): Promise<number> {
    return this.$commonDialogs.confirmation.show({
      message: [
        "空の定型文がありますがそれもインポートしますか？",
      ],
      buttons: [
        {
          kind: "confirm",
          label: "インポートする",
        },
        {
          kind: "confirm",
          label: "空の定型文以外をインポートする",
        },
        {
          kind: "cancel",
          label: "中止する",
        },
      ],
    });
  }

  private extractEmptyTemplate(data: IFixedPhraseFolderRootContent): IFixedPhraseFolderRootContent {
    return {
      tableId: data.tableId,
      templateId: data.templateId,
      folders: this.extractEmptyPhraseAndFolder(data.folders),
    };
  }

  private extractEmptyPhraseAndFolder(folders: IFixedPhraseFolderContent[]): IFixedPhraseFolderContent[] {
    return Enumerable.from(folders)
      .select(f => this.toExtractedFixedPhraseFolderContent(f))
      .where(f => !!f)
      .toArray();
  }

  private toExtractedFixedPhraseFolderContent(folder: IFixedPhraseFolderContent): IFixedPhraseFolderContent {
    const folders = this.extractEmptyPhraseAndFolder(folder.folders);
    const phrase = folder.phrase.filter(p => !!p.text);
    return ((folders && folders.length > 0) || (phrase && phrase.length > 0))
      ? {
        name: folder.name,
        folders: folders,
        phrase: phrase,
      }
      : null;
  }
}
